import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubmittalReviewModal = ({ submittal, onClose, onUpdate }) => {
  const [status, setStatus] = useState('');
  const [comments, setComments] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  // Determine user's role in this submittal
  const isInitiator = user?.id === submittal.assigned_by;
  let reviewerNumber = null;
  if (user?.id === submittal.assigned_to) reviewerNumber = 1;
  else if (user?.id === submittal.assigned_to_2) reviewerNumber = 2;
  else if (user?.id === submittal.assigned_to_3) reviewerNumber = 3;

  const isMultiUser = submittal.assigned_to_2 || submittal.assigned_to_3;
  const isSequential = submittal.workflow_type === 'sequential';
  const currentReviewer = submittal.current_reviewer || 1;
  const isPendingResubmission = submittal.status === 'pending_resubmission';

  // Determine if user can take action
  const canReview = reviewerNumber && (!isSequential || reviewerNumber === currentReviewer) && !isPendingResubmission;
  const canResubmit = isInitiator && isPendingResubmission;

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;

    setUploading(true);
    const fileData = new FormData();
    fileData.append('file', selectedFile);
    fileData.append('document_type', 'submittals');
    fileData.append('project_id', submittal.project_id);

    try {
      const response = await axios.post(`${API}/upload`, fileData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { url: response.data.file_url, name: selectedFile.name };
    } catch (error) {
      toast.error('File upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let fileData = null;
      
      if (selectedFile) {
        fileData = await uploadFile();
      }

      const params = {
        review_comments: comments,
      };
      
      if (fileData) {
        params.file_url = fileData.url;
        params.file_name = fileData.name;
      }
      
      if (canResubmit) {
        // Initiator resubmitting
        params.action_type = 'resubmission';
      } else if (canReview) {
        // Reviewer submitting review
        params.status = status;
        params.action_type = 'review';
        if (isMultiUser && reviewerNumber) {
          params.reviewer_number = reviewerNumber;
        }
      }

      await axios.patch(`${API}/submittals/${submittal.id}`, null, { params });

      toast.success(canResubmit ? 'Submittal resubmitted successfully' : 'Review submitted successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to submit');
    }
  };

  // Build reviewers list
  const reviewers = [
    { name: submittal.assigned_to_name, approval: submittal.approval_1, comments: submittal.approval_1_comments, num: 1 },
    submittal.assigned_to_2 ? { name: submittal.assigned_to_2_name, approval: submittal.approval_2, comments: submittal.approval_2_comments, num: 2 } : null,
    submittal.assigned_to_3 ? { name: submittal.assigned_to_3_name, approval: submittal.approval_3, comments: submittal.approval_3_comments, num: 3 } : null,
  ].filter(Boolean);

  // Response history
  const responseHistory = submittal.response_history || [];
  const attachments = submittal.attachments || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {canResubmit ? 'Resubmit Submittal' : 'Review Submittal'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">{submittal.title}</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Submittal Info */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Raised By:</span>
                <span className="ml-2 font-medium">{submittal.assigned_by_name}</span>
              </div>
              <div>
                <span className="text-slate-500">Due Date:</span>
                <span className="ml-2 font-medium">{new Date(submittal.due_date).toLocaleDateString()}</span>
              </div>
              <div>
                <span className="text-slate-500">Workflow:</span>
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                  isSequential ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {submittal.workflow_type || 'simultaneous'}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Status:</span>
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                  submittal.status === 'closed' ? 'bg-green-100 text-green-700' :
                  submittal.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  submittal.status === 'pending_resubmission' ? 'bg-amber-100 text-amber-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {submittal.status?.replace('_', ' ')}
                </span>
              </div>
            </div>
            {submittal.description && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-sm text-slate-600">{submittal.description}</p>
              </div>
            )}
          </div>

          {/* Reviewers Status */}
          {isMultiUser && (
            <div className="border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Reviewers Status</h3>
              <div className="space-y-2">
                {reviewers.map((r, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        isSequential && currentReviewer === r.num ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {r.num}
                      </span>
                      <span className="font-medium">{r.name}</span>
                      {isSequential && currentReviewer === r.num && !isPendingResubmission && (
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">current</span>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      r.approval === 'approved' ? 'bg-green-100 text-green-700' :
                      r.approval === 'rejected' ? 'bg-red-100 text-red-700' :
                      r.approval === 'resubmit' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {r.approval || 'pending'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Response History */}
          {responseHistory.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Response History</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {responseHistory.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border-l-4 border-l-slate-300">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{entry.user_name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          entry.response_type === 'resubmission' ? 'bg-blue-100 text-blue-700' :
                          entry.status === 'approved' ? 'bg-green-100 text-green-700' :
                          entry.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          entry.status === 'resubmit' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {entry.response_type === 'resubmission' ? 'Resubmitted' : entry.status || entry.response_type}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                    </div>
                    {entry.comments && (
                      <p className="text-sm text-slate-600">{entry.comments}</p>
                    )}
                    {entry.file_url && (
                      <a 
                        href={entry.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {entry.file_name || 'Attachment'}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">All Attachments</h3>
              <div className="grid grid-cols-2 gap-2">
                {attachments.map((att, idx) => (
                  <a 
                    key={idx}
                    href={att.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    download
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                  >
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{att.file_name}</div>
                      <div className="text-xs text-slate-500">by {att.uploaded_by}</div>
                    </div>
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Action Form */}
          {(canReview || canResubmit) && (
            <form onSubmit={handleSubmit} className="space-y-4 border-t border-slate-200 pt-6">
              <h3 className="text-sm font-semibold text-slate-900">
                {canResubmit ? 'Provide Response and Resubmit' : 'Submit Your Review'}
              </h3>
              
              {canReview && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Review Status *</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                  >
                    <option value="">Select a status</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="resubmit">Request Resubmission</option>
                  </select>
                  {status === 'resubmit' && (
                    <p className="text-xs text-amber-600 mt-1">
                      This will reassign the submittal back to the initiator for revisions.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {canResubmit ? 'Response / Comments *' : 'Review Comments *'}
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  rows="4"
                  placeholder={canResubmit ? 'Provide your response to the reviewer feedback...' : 'Add your review comments here...'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Attach File {canResubmit ? '(Revised Document)' : '(Optional)'}
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.dwg,.dxf"
                />
                {selectedFile && (
                  <p className="text-xs text-slate-600 mt-1">Selected: {selectedFile.name}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={uploading || (canReview && !status)}
                  className={`flex-1 text-white py-2 rounded-md disabled:opacity-50 ${
                    canResubmit ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                >
                  {uploading ? 'Uploading...' : canResubmit ? 'Resubmit' : 'Submit Review'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Read-only mode message */}
          {!canReview && !canResubmit && (
            <div className="text-center py-4 text-slate-500 border-t border-slate-200">
              {isPendingResubmission 
                ? 'Waiting for the initiator to resubmit.'
                : submittal.status === 'closed'
                ? 'This submittal has been closed.'
                : 'You cannot take action on this submittal at this time.'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full border border-slate-200 py-2 rounded-md hover:bg-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
